import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Loader2, Heart, ArrowLeft, Mail, Copy, Check } from 'lucide-react';
import Header from '../components/Header';
import SEOHead from '../components/SEOHead';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Public read-only inspo list — anyone with the slug can view.
 * Hydrates each item_ref by fetching the inspo item itself so we render the actual media.
 */
const InspoListPublicPage = () => {
  const { slug } = useParams();
  const [list, setList] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/inspo-lists/${encodeURIComponent(slug)}`);
        if (r.status === 404) { if (mounted) { setNotFound(true); setLoading(false); } return; }
        const data = await r.json();
        if (!mounted) return;
        setList(data);
        // Hydrate the inspo items — fetch the full inspo feed once and filter (cheap)
        const allRes = await fetch(`${API_URL}/api/inspo/items?limit=500`);
        const allData = await allRes.json();
        const wantedIds = new Set((data.item_refs || []).filter(r => r.item_type === 'inspo').map(r => r.item_id));
        const matched = (allData.items || []).filter(i => wantedIds.has(i.id));
        // Preserve the order from the list
        const orderMap = new Map();
        (data.item_refs || []).forEach((r, idx) => orderMap.set(r.item_id, idx));
        matched.sort((a, b) => (orderMap.get(a.id) ?? 0) - (orderMap.get(b.id) ?? 0));
        if (mounted) setItems(matched);
      } catch (e) {
        if (mounted) setNotFound(true);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [slug]);

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(window.location.href); setCopied(true); setTimeout(() => setCopied(false), 1800); } catch { /* ignore */ }
  };

  if (loading) {
    return <div className="min-h-screen bg-white flex items-center justify-center"><Loader2 className="animate-spin" /></div>;
  }
  if (notFound || !list) {
    return (
      <div className="min-h-screen bg-white text-black">
        <Header variant="light" />
        <div className="pt-32 px-6 text-center">
          <p className="text-[10px] tracking-[0.3em] uppercase text-black/40 mb-4">Crystal · Inspo</p>
          <h1 className="font-presti text-3xl md:text-4xl mb-4">List not found</h1>
          <Link to="/inspo" className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-black/60 hover:text-black border-b border-black/30">
            <ArrowLeft size={14} /> Browse inspo
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white text-black min-h-screen" data-testid="inspo-list-public">
      <SEOHead
        title={`${list.name} — Crystal Media Inspo`}
        description={list.note || `A wedding inspo list shared via Crystal Media.`}
        url={`/inspo/list/${list.slug}`}
      />
      <Header variant="light" />

      <section className="pt-28 md:pt-32 pb-10 px-6 text-center max-w-3xl mx-auto">
        <p className="text-[10px] tracking-[0.3em] uppercase text-black/40 mb-4">Inspo List · Shared</p>
        <h1 className="font-presti text-3xl sm:text-4xl md:text-5xl tracking-[0.02em] leading-[1.1] mb-4 lowercase" data-testid="inspo-list-name">{list.name}</h1>
        {list.note && <p className="text-black/55 text-sm md:text-base leading-relaxed mb-6 italic">&ldquo;{list.note}&rdquo;</p>}

        <div className="flex items-center justify-center gap-3 flex-wrap mt-2">
          <button
            type="button"
            onClick={copyLink}
            className="inline-flex items-center gap-2 px-5 py-2.5 border border-black/20 text-xs tracking-[0.2em] uppercase text-black/70 hover:border-black hover:text-black transition-colors"
            data-testid="inspo-list-copy-link"
          >
            {copied ? <><Check size={14} /> Link copied</> : <><Copy size={14} /> Copy share link</>}
          </button>
          <Link
            to="/inquire"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-black text-white text-xs tracking-[0.2em] uppercase hover:bg-black/80 transition-colors"
            data-testid="inspo-list-inquire"
          >
            <Mail size={14} /> Send this to Crystal Media
          </Link>
        </div>
      </section>

      <section className="px-3 md:px-6 pb-24 max-w-7xl mx-auto">
        {items.length === 0 ? (
          <p className="text-center text-black/40 text-sm py-10">This list is empty.</p>
        ) : (
          <div className="columns-2 md:columns-3 lg:columns-4 xl:columns-5 gap-3 md:gap-4" data-testid="inspo-list-grid">
            {items.map(item => (
              <div key={item.id} className="mb-3 md:mb-4 break-inside-avoid relative">
                <div className="relative overflow-hidden bg-black/5">
                  {item.media_type === 'video' ? (
                    <video src={item.media_url} poster={item.thumbnail_url || undefined} className="w-full h-auto block" muted loop playsInline preload="metadata" />
                  ) : (
                    <img src={item.media_url} alt="" className="w-full h-auto block" loading="lazy" />
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <footer className="py-10 px-6 border-t border-black/5 text-center">
        <p className="text-[10px] tracking-[0.2em] uppercase text-black/40">
          Inspo curated by <Link to="/" className="hover:text-black">Crystal Media</Link>
        </p>
      </footer>
    </div>
  );
};

export default InspoListPublicPage;
